import React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import App from "./App";
import { installFetchInterceptor } from "./lib/api";
import { installChunkErrorHandlers, checkBuildHash, clearReloadGuards } from "./lib/versionCheck";
import "./styles/tokens.css";
import "./styles/index.css";
import "./styles/terminal.css";
// Preload TopBar chrome icons into iconify's in-memory cache before first render.
// Prevents the empty-button flash at <xl viewports where icon-only triggers are shown.
import "./lib/iconifyBootstrap";

// ── Synchronous Theme Initialization ──────────────────────────────────────────
// Apply theme from cookie BEFORE React renders to prevent flash on page refresh.
// ThemeProvider will take over once mounted, but this ensures first paint is correct.
const VALID_THEMES = ["defaulted", "smoked", "spectre", "reel"] as const;
const themeCookie = document.cookie
  .split(";")
  .map((c) => c.trim())
  .find((c) => c.startsWith("qb_theme="))
  ?.split("=")[1];

const initialTheme = VALID_THEMES.includes(themeCookie as (typeof VALID_THEMES)[number])
  ? themeCookie
  : "spectre";

document.body.className = `theme-${initialTheme}`;

// Install fetch interceptor for subpath deployments (e.g., /v4-dashboard/)
// This rewrites /api/*, /auth/*, etc. to include the base path
installFetchInterceptor();

// Install chunk error handlers BEFORE React renders. When a lazy-loaded
// chunk 404s (stale bundle after a dashboard update), this auto-reloads
// the page once instead of showing a broken UI.
installChunkErrorHandlers();

const container = document.getElementById("root");
if (!container) {
  throw new Error("Root element not found");
}

// Base path for subpath deployments (e.g., /v4-dashboard/)
// Vite sets import.meta.env.BASE_URL from the `base` config option
const basename = import.meta.env.BASE_URL.replace(/\/$/, "") || undefined;

createRoot(container).render(
  <React.StrictMode>
    <BrowserRouter basename={basename}>
      <App />
    </BrowserRouter>
  </React.StrictMode>
);

// After the app renders, check the server's build hash against the one
// compiled into this bundle. If they differ, the browser is running stale
// JS — reload once. This is async and non-blocking; failures are silent.
checkBuildHash().then(() => {
  // If we got here without triggering a reload, the bundle is current.
  // Clear reload-loop guards so future mismatches can trigger a new cycle.
  // The stale-detected flag (read by LoginPage) is cleared separately
  // when the login page consumes it.
  clearReloadGuards();
});
